import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Calendar, ChevronRight, Star, ShieldCheck, Gem, Menu, X, Clock, Users, Phone, Mail, ChevronDown } from 'lucide-react';
import { ARTISTS, CATEGORIES } from '../data/artists';

function Home() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [showAllArtists, setShowAllArtists] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth > 768) setMobileNavOpen(false); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? 'hidden' : '';
  }, [mobileNavOpen]);

  useEffect(() => { setShowAllArtists(false); }, [activeCategory]);

  const filteredArtists = useMemo(() => {
    let list = activeCategory === 'All' ? ARTISTS : ARTISTS.filter(a => a.category === activeCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = ARTISTS.filter(a => a.name.toLowerCase().includes(q) || a.genre.toLowerCase().includes(q));
    }
    return list;
  }, [activeCategory, searchQuery]);

  const displayedArtists = showAllArtists ? filteredArtists : filteredArtists.slice(0, 20);

  const handleArtistClick = (slug) => {
    navigate(`/book/${slug}`);
  };

  const scrollTo = (id) => {
    setMobileNavOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const getInitials = (name) => {
    return name.split(' ').filter(w => !['&', 'the', 'of', 'feat.', 'feat', 'featuring'].includes(w.toLowerCase()))
      .slice(0, 2).map(w => w[0]).join('').toUpperCase();
  };

  const getColor = (name) => {
    const colors = ['#8B6914','#6B4C11','#9A7B2F','#7D6122','#A68B3D','#5C4210','#B09548','#4D3A0E','#C9A84C','#3E310B'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="app-container">

      {/* ───── Mobile Nav Overlay ───── */}
      <div className={`mobile-nav ${mobileNavOpen ? 'open' : ''}`}>
        <button className="mobile-nav-close" onClick={() => setMobileNavOpen(false)} aria-label="Close menu">
          <X size={28} />
        </button>
        <a href="#about" onClick={() => scrollTo('about')}>About Us</a>
        <a href="#talent" onClick={() => scrollTo('talent')}>Roster</a>
        <a href="#booking" onClick={() => scrollTo('booking')}>Booking</a>
        <a href="#top20" onClick={() => scrollTo('top20')}>Top 20</a>
        <button className="btn btn-primary" onClick={() => scrollTo('booking')}>
          Book Now
        </button>
      </div>

      {/* ───── Navbar ───── */}
      <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container navbar-inner">
          <div className="logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ cursor: 'pointer' }}>
            <Gem size={22} color="var(--accent-gold)" />
            <span>PYRAMID</span>
            <span className="gold">ENTERTAINMENT</span>
          </div>
          <div className="nav-links">
            <a href="#about" onClick={(e) => { e.preventDefault(); scrollTo('about'); }}>About</a>
            <a href="#talent" onClick={(e) => { e.preventDefault(); scrollTo('talent'); }}>Roster</a>
            <a href="#booking" onClick={(e) => { e.preventDefault(); scrollTo('booking'); }}>Booking</a>
            <a href="#top20" onClick={(e) => { e.preventDefault(); scrollTo('top20'); }}>Top 20</a>
            <button className="btn btn-primary" style={{ padding: '8px 20px', fontSize: '0.85rem' }}
              onClick={() => scrollTo('booking')}>
              Book Now
            </button>
          </div>
          <button className="mobile-menu-btn" onClick={() => setMobileNavOpen(true)} aria-label="Open menu">
            <Menu size={26} />
          </button>
        </div>
      </nav>

      {/* ───── Hero ───── */}
      <header className="hero">
        <div className="hero-bg" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/concert_background.png)` }}></div>
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-content">
            <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <span className="hero-badge">Full Service Talent Booking Agency</span>
            </div>
            <h1 className="animate-fade-in" style={{ animationDelay: '0.4s' }}>
              Book World-Class<br />
              <span className="gold-gradient">Entertainment</span>
            </h1>
            <p className="hero-subtitle animate-fade-in" style={{ animationDelay: '0.6s' }}>
              For over 30 years, Pyramid Entertainment Group has provided a wide range of entertainment for concert events, corporate events, casinos, fairs, festivals and more.
            </p>

            <div className="glass-panel search-bar animate-fade-in" style={{ animationDelay: '0.8s' }}>
              <div className="search-input-wrap">
                <Search size={18} color="var(--text-muted)" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search any artist..."
                />
              </div>
              <button className="btn btn-primary" style={{ padding: '14px 24px', whiteSpace: 'nowrap' }}
                onClick={() => scrollTo('talent')}>
                Search Roster <ChevronRight size={16} />
              </button>
            </div>

            <div className="hero-features animate-fade-in" style={{ animationDelay: '1s' }}>
              <span className="hero-feature"><ShieldCheck size={15} color="var(--accent-gold)" /> 30+ Years Experience</span>
              <span className="hero-feature"><Star size={15} color="var(--accent-gold)" /> 150+ Artists</span>
              <span className="hero-feature"><Users size={15} color="var(--accent-gold)" /> Global Events</span>
            </div>
          </div>
        </div>
      </header>

      {/* ───── About / Trust ───── */}
      <section className="about-section" id="about">
        <div className="container">
          <div className="about-layout">
            <div className="about-text">
              <h2>Welcome to <span className="gold-gradient">Pyramid Entertainment Group</span></h2>
              <p>
                For over 30 years, Pyramid Entertainment Group has served as an international talent agency providing
                a wide range of entertainment for concert events, corporate/private events, casinos, fairs, festivals and more.
              </p>
              <p>
                Our artists range from legendary and contemporary R&B to Funk, Hip-Hop, Rap, Pop, Jazz, Rock and Gospel.
                Event planners, corporations and concert industry professionals rely on Pyramid Entertainment for their live music needs.
              </p>
              <p>
                Our veteran agents are happy to assist you in selecting the ideal artist(s) for your next event.
              </p>
            </div>
            <div className="about-stats">
              <div className="stat-card glass-panel">
                <div className="stat-number">30+</div>
                <div className="stat-label">Years in Business</div>
              </div>
              <div className="stat-card glass-panel">
                <div className="stat-number">150+</div>
                <div className="stat-label">Artists Available</div>
              </div>
              <div className="stat-card glass-panel">
                <div className="stat-number">1000+</div>
                <div className="stat-label">Events Booked</div>
              </div>
              <div className="stat-card glass-panel">
                <div className="stat-number">50+</div>
                <div className="stat-label">Countries Reached</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── Top 20 ───── */}
      <section className="top20-section" id="top20">
        <div className="container">
          <h2>Top 20 <span className="gold-gradient">Most Requested</span></h2>
          <p className="section-subtitle">Our most in-demand artists for live bookings</p>
          <div className="top20-grid">
            {ARTISTS.filter(a => a.featured).map((artist, i) => (
              <div className="top20-item" key={artist.name} onClick={() => handleArtistClick(artist.slug)}>
                <span className="top20-rank">{String(i + 1).padStart(2, '0')}</span>
                <div className="top20-info">
                  <span className="top20-name">{artist.name}</span>
                  <span className="top20-genre">{artist.genre}</span>
                </div>
                <button className="top20-book" onClick={(e) => { e.stopPropagation(); handleArtistClick(artist.slug); }}>
                  Book <ChevronRight size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── Full Roster ───── */}
      <section className="roster-section" id="talent">
        <div className="container">
          <h2>Full Talent <span className="gold-gradient">Roster</span></h2>
          <p className="section-subtitle">Access artists across every genre for your next event. Click any artist to inquire.</p>

          <div className="roster-tabs">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`roster-tab ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => { setActiveCategory(cat); setSearchQuery(''); }}
              >
                {cat}
                {cat !== 'All' && <span className="tab-count">{ARTISTS.filter(a => a.category === cat).length}</span>}
              </button>
            ))}
          </div>

          <div className="artist-grid">
            {displayedArtists.map((artist) => (
              <div className="artist-card" key={artist.name} onClick={() => handleArtistClick(artist.slug)}>
                <div 
                  className="artist-card-bg"
                  style={{
                    width: '46px',
                    height: '46px',
                    borderRadius: '8px',
                    flexShrink: 0,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundColor: artist.image ? 'transparent' : getColor(artist.name),
                    backgroundImage: artist.image ? `url(${artist.image})` : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--accent-gold-light)',
                    fontWeight: 700,
                    fontSize: '0.85rem'
                  }}
                >
                  {!artist.image && getInitials(artist.name)}
                </div>
                <div className="artist-card-info">
                  <div className="artist-card-name" style={{ fontSize: '0.95rem' }}>{artist.name}</div>
                  <div className="artist-card-genre" style={{ opacity: 0.8 }}>{artist.genre}</div>
                </div>
                <div className="artist-card-action">
                  <ChevronRight size={16} />
                </div>
              </div>
            ))}
          </div>

          {!showAllArtists && filteredArtists.length > 20 && (
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button className="btn btn-primary" onClick={() => setShowAllArtists(true)}>
                Show All {filteredArtists.length} Artists <ChevronDown size={16} />
              </button>
            </div>
          )}

          {filteredArtists.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
              <p>No artists found matching "{searchQuery}". <button style={{ color: 'var(--accent-gold)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-family)' }} onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}>Clear search</button></p>
            </div>
          )}
        </div>
      </section>

      {/* ───── Booking Funnel ───── */}
      <section className="booking-section" id="booking">
        <div className="booking-glow"></div>
        <div className="container">
          <div className="booking-layout">

            <div className="booking-left">
              <h2>Book Your <span className="gold-gradient">Event</span></h2>
              <p>Submit your inquiry below and one of our veteran agents will contact you to assist in selecting the ideal artist(s) for your next event.</p>

              <div className="feature-card glass-panel">
                <div className="feature-icon"><Calendar color="var(--accent-gold)" size={22} /></div>
                <div>
                  <h4>Concert & Festival Booking</h4>
                  <p>From arenas to outdoor festivals — we handle it all.</p>
                </div>
              </div>
              <div className="feature-card glass-panel">
                <div className="feature-icon"><Users color="var(--accent-gold)" size={22} /></div>
                <div>
                  <h4>Corporate & Private Events</h4>
                  <p>Holiday parties, galas, product launches and VIP celebrations.</p>
                </div>
              </div>
              <div className="feature-card glass-panel">
                <div className="feature-icon"><Clock color="var(--accent-gold)" size={22} /></div>
                <div>
                  <h4>Casinos, Fairs & Festivals</h4>
                  <p>Turnkey entertainment solutions for any venue or occasion.</p>
                </div>
              </div>
              <div className="feature-card glass-panel">
                <div className="feature-icon"><Phone color="var(--accent-gold)" size={22} /></div>
                <div>
                  <h4>Veteran Agent Support</h4>
                  <p>Over 30 years of industry relationships at your service.</p>
                </div>
              </div>
            </div>

            <div className="booking-right">
              <div className="glass-panel booking-form">
                <h3>Booking Inquiry</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name</label>
                    <input type="text" placeholder="Your name" />
                  </div>
                  <div className="form-group">
                    <label>Company / Organization</label>
                    <input type="text" placeholder="Company name" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" placeholder="you@company.com" />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input type="tel" placeholder="(555) 000-0000" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Artist(s) Requested</label>
                  <input type="text" placeholder="e.g. Kool & the Gang, Flo Rida" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Event Date</label>
                    <input type="date" />
                  </div>
                  <div className="form-group">
                    <label>Event Type</label>
                    <select defaultValue="">
                      <option value="" disabled>Select type</option>
                      <option>Concert / Festival</option>
                      <option>Corporate Event</option>
                      <option>Private Party / Gala</option>
                      <option>Casino</option>
                      <option>Fair / County Event</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Event Details</label>
                  <textarea placeholder="Venue, number of guests, budget range, special requirements..." />
                </div>
                <button className="btn btn-primary" style={{ width: '100%', padding: '16px', fontSize: '1.05rem', marginTop: '5px' }}>
                  Submit Booking Request
                </button>
                <p className="form-note">Our team typically responds within 24 hours.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ───── Footer ───── */}
      <footer className="site-footer">
        <div className="container">
          <div className="footer-top">
            <div className="footer-logo-section">
              <div className="footer-logo">
                <Gem size={18} color="var(--accent-gold)" />
                <span>PYRAMID</span>
                <span className="gold">ENTERTAINMENT</span>
              </div>
              <p className="footer-tagline">Full Service Talent Booking Agency</p>
            </div>
            <div className="footer-links">
              <a href="#about" onClick={(e) => { e.preventDefault(); scrollTo('about'); }}>About Us</a>
              <a href="#talent" onClick={(e) => { e.preventDefault(); scrollTo('talent'); }}>Roster</a>
              <a href="#booking" onClick={(e) => { e.preventDefault(); scrollTo('booking'); }}>Booking</a>
              <a href="#top20" onClick={(e) => { e.preventDefault(); scrollTo('top20'); }}>Top 20</a>
            </div>
            <div className="footer-contact">
              <a href="mailto:info@pyramid-ent.com" className="footer-contact-item">
                <Mail size={14} /> info@pyramid-ent.com
              </a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2026 Pyramid Entertainment Group. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
